import { BackendAdapterBragg, InitResponses } from "@spawn/backend-adapter-bragg";
import { consumer, inject } from "@spawn/core";
import { IBraggResponseFreegameEvent, InitActionExtendedBragg } from "@spawn/freegames";
import { PaylineType, ResumeType } from "@spawn/networking";
import {
	BraggSlotfaceRestoreService,
	ILevelDataBragg,
	ILevelsDataBragg,
	RevealUncoverTier,
	ensurePaddingMysteries,
	parseRevealBraggNetworkState,
	replaceRevealPositionsWithMysteries,
} from "@spawn/reveal";
import { BaseSymbolTypes, symbolTypeBraggIds } from "../../services/symbolService";
import { CacheKeys, CacheStorage } from "../../utils/cacheStorage";
import { deepClone } from "../../utils/object";
import { getReelsAfterReveal } from "../hacks";
import { getWonFGScatterCount } from "../helpers";
import { IGameNetworkState } from "../networkState";
import { INrgBraggSlotOriginal } from "../types/bragg";
import { ensure4BlockMysteries, nrgApplyBraggSlotfaceBeforeRestore, nrgGenerateBraggNudgingOffsets } from "./helpers";
import { UncoverTierService } from "../../services/uncoverTierService";

@consumer
export class ExtendedInitActionBragg extends InitActionExtendedBragg {
	@inject protected readonly uncoverTierService!: UncoverTierService;
	@inject protected readonly braggSlotfaceRestoreService!: BraggSlotfaceRestoreService;

	protected parse({ auth, playerSettings, openGame, initGame }: InitResponses, adapter: BackendAdapterBragg): IGameNetworkState {
		const result = super.parse({ auth, playerSettings, openGame, initGame }, adapter) as IGameNetworkState;
		const lastEvent = (initGame as unknown as IBraggResponseFreegameEvent).body.lastEvent;
		const isResumingSpin = result.session.resumeType === ResumeType.Spin || result.session.resumeType === ResumeType.FreeGame;

		const feature: Partial<IGameNetworkState> = {};
		const lastEventData = lastEvent?.data as { SLOT_ORIGINAL?: INrgBraggSlotOriginal } | undefined;
		const slotOriginal = (lastEventData?.SLOT_ORIGINAL ??
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(initGame.body as any)?.data?.SLOT_ORIGINAL) as INrgBraggSlotOriginal | undefined;
		feature.fgTriggerState = this.extractFGTriggerState(
			slotOriginal,
			result.slotGameBase.selectedBet,
			this.uncoverTierService.fromWinFactor(result.slotGameBase.winFactor)
		);

		// Extract level data from different sources
		const { levelsData, levelData } = this.extractLevelData(lastEvent || initGame.body, result.slotGameBase.selectedBet);

		// Set all level data for the restore service
		if (levelsData && Object.keys(levelsData).length) {
			this.braggSlotfaceRestoreService.setAllLevelData(levelsData);
		}

		// Process level data if available
		if (levelData?.slotFace && levelData?.reveals) {
			this.processLevelData(levelData, result, feature, this.uncoverTierService.fromWinFactor(result.slotGameBase.winFactor));
		}

		if (isResumingSpin) {
			result.initialResumeReels = getReelsAfterReveal(result.slotGameBase.reels, feature.reveal?.symbols ?? []); // weird and backwards but qa insists on this

			if (result.slotGameFreeGame?.currentRound === 1) {
				const firstFGResumeResponse = deepClone({
					...initGame,
					// @ts-ignore
					body: { ...initGame.body, ...initGame.body.lastEvent, lastEvent: null },
				});
				CacheStorage.set(CacheKeys.FirstFGResumeResponse, firstFGResumeResponse);
			} else if (getWonFGScatterCount(feature.reveal) > 0) {
				feature.preNudgeUpReels = result.slotGameBase.reels;
			}
		}

		return {
			...result,
			slotGameBase: {
				...result.slotGameBase,
				paylines: result.slotGameBase.paylines.filter(({ type }) => type !== PaylineType.Scatter),
			},
			...feature,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private extractLevelData(lastEvent: any, selectedBet: number): { levelsData?: ILevelsDataBragg; levelData?: ILevelDataBragg } {
		let levelsData: ILevelsDataBragg | undefined;
		let levelData: ILevelDataBragg | undefined;
		const level = String(selectedBet);

		// Check for SLOT level data first
		if (lastEvent?.data?.SLOT?.levelData) {
			const slotLevelsData = lastEvent.data.SLOT.levelData as ILevelsDataBragg;
			levelsData = slotLevelsData;
			levelData = slotLevelsData[level];
		}
		// Fall back to FREE_SPIN data
		else if (lastEvent?.data.FREE_SPIN) {
			const freespinData = lastEvent.data.FREE_SPIN as unknown as ILevelDataBragg;
			levelData = freespinData;
			levelsData = {
				[level]: freespinData,
			};
		}

		const initialSlotFace = lastEvent?.data?.SLOT?.slotFace;
		if (!levelData?.slotFace && Array.isArray(initialSlotFace)) {
			levelData = {
				level,
				reveals: [],
				slotFace: deepClone(initialSlotFace),
			};
			levelsData = {
				...(levelsData ?? {}),
				[level]: levelData,
			};
		}

		return { levelsData, levelData };
	}

	private processLevelData(
		levelData: ILevelDataBragg,
		result: IGameNetworkState,
		feature: Partial<IGameNetworkState>,
		uncoverTier: RevealUncoverTier
	): void {
		const slotFace = deepClone(levelData.slotFace);
		nrgApplyBraggSlotfaceBeforeRestore(result.slotGameBase.reels, slotFace, symbolTypeBraggIds);
		ensurePaddingMysteries(result.slotGameBase.reels, levelData.reveals, BaseSymbolTypes.Mystery);
		replaceRevealPositionsWithMysteries(result.slotGameBase.reels, levelData.reveals, BaseSymbolTypes.Mystery);

		feature.reveal = parseRevealBraggNetworkState(levelData.reveals, symbolTypeBraggIds, uncoverTier);
		feature.nudging = nrgGenerateBraggNudgingOffsets(levelData.reveals);

		if (feature.nudging.some((nudge) => nudge < 0)) {
			ensure4BlockMysteries(result.slotGameBase.reels);
		}
	}

	private extractFGTriggerState(
		slotOriginal: INrgBraggSlotOriginal | undefined,
		selectedBet: number,
		uncoverTier: RevealUncoverTier
	): IGameNetworkState["fgTriggerState"] {
		if (!slotOriginal?.slotFace) {
			return undefined;
		}

		const reveals = this.getSlotOriginalReveals(slotOriginal, selectedBet);
		return {
			reels: slotOriginal.slotFace.map((reel) => reel.map((symbolId) => symbolTypeBraggIds.get(symbolId) as string)),
			reveal: parseRevealBraggNetworkState(reveals, symbolTypeBraggIds, uncoverTier),
			nudging: nrgGenerateBraggNudgingOffsets(reveals),
		};
	}

	private getSlotOriginalReveals(slotOriginal: INrgBraggSlotOriginal, selectedBet: number): ILevelDataBragg["reveals"] {
		const levelsData = slotOriginal.levelData;
		if (!levelsData) {
			return [];
		}

		const selectedLevel = levelsData[String(selectedBet)];
		if (selectedLevel?.reveals) {
			return selectedLevel.reveals;
		}

		const fallbackLevel = Object.values(levelsData).find((level) => level?.reveals);
		return fallbackLevel?.reveals ?? [];
	}
}
